import { reactive } from "vue"
import { hapticError, hapticSuccess, hapticWarning } from "../services/hapticsService"

export type ToastVariant = "success" | "error" | "info" | "warning"

export interface Toast {
  id: number
  message: string
  variant: ToastVariant
  duration: number
  action?: {
    label: string
    handler: () => void
  }
}

interface ToastState {
  toasts: Toast[]
}

const state = reactive<ToastState>({
  toasts: [],
})

let nextId = 1

function show(message: string, options: { variant?: ToastVariant; duration?: number; action?: Toast["action"] } = {}) {
  const toast: Toast = {
    id: nextId++,
    message,
    variant: options.variant ?? "success",
    duration: options.duration ?? 2500,
    action: options.action,
  }
  state.toasts.push(toast)

  // Toasts are the app-wide feedback channel, so this is the one place that
  // gives every save/delete/failure a matching haptic without per-view wiring.
  if (toast.variant === "success") hapticSuccess()
  else if (toast.variant === "error") hapticError()
  else if (toast.variant === "warning") hapticWarning()

  if (toast.duration > 0) {
    setTimeout(() => dismiss(toast.id), toast.duration)
  }

  return toast.id
}

function dismiss(id: number) {
  const idx = state.toasts.findIndex((t) => t.id === id)
  if (idx !== -1) state.toasts.splice(idx, 1)
}

export interface ToastOptions {
  duration?: number
  action?: Toast["action"]
}

export function useToast() {
  return {
    toasts: state.toasts,
    show,
    dismiss,
    success: (message: string, options: ToastOptions = {}) => show(message, { ...options, variant: "success" }),
    error: (message: string, options: ToastOptions = {}) =>
      show(message, { ...options, variant: "error", duration: options.duration ?? 4000 }),
    info: (message: string, options: ToastOptions = {}) => show(message, { ...options, variant: "info" }),
    warning: (message: string, options: ToastOptions = {}) => show(message, { ...options, variant: "warning" }),
  }
}
